/**
 * MayaAvatar — Avatar reativo da Maya com variantes faciais e
 * transição suave via Framer Motion (cross-fade + bounce em celebrating).
 *
 * Variantes disponíveis:
 *  - happy        → padrão (sorriso amistoso)
 *  - thinking     → durante análise / loading
 *  - alert        → banners de risco, rejeição eSocial
 *  - celebrating  → após ação bem-sucedida
 *
 * Tamanhos:
 *  - xs (24px)  - usado em badges/inline
 *  - sm (40px)  - botões e listas
 *  - md (64px)  - dialogs e headers
 *  - lg (96px)  - banners de destaque
 *  - xl (160px) - tela cheia / onboarding
 *
 * Exemplo de uso:
 *   <MayaAvatar variant="alert" size="md" data-testid="maya-avatar" />
 *   <MayaAvatar variant="celebrating" size="lg" pulse />
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const VARIANTS = {
  happy:       '/maya/happy.jpeg',
  thinking:    '/maya/thinking.jpeg',
  alert:       '/maya/alert.jpeg',
  celebrating: '/maya/celebrating.jpeg',
};

const SIZE_PX = {
  xs: 24, sm: 40, fab: 48, md: 64, lg: 96, xl: 160,
};

const RING_BY_VARIANT = {
  happy:       'ring-indigo-200',
  thinking:    'ring-amber-200',
  alert:       'ring-red-300',
  celebrating: 'ring-emerald-300',
};

const STATUS_DOT_COLOR = {
  happy:       'bg-emerald-500',
  thinking:    'bg-amber-500',
  alert:       'bg-red-500',
  celebrating: 'bg-emerald-500',
};

// Cada variante tem sua animação característica de "entrada"
const ENTRY_ANIMATION = {
  happy:       { scale: [0.9, 1.04, 1], rotate: [0, 0, 0] },
  thinking:    { scale: [0.98, 1.02, 1], rotate: [0, -2, 0] },
  alert:       { scale: [1.1, 0.95, 1.05, 1], rotate: [0, 1, -1, 0] },
  celebrating: { scale: [0.7, 1.2, 0.95, 1.05, 1], rotate: [0, 6, -4, 2, 0] },
};

const MayaAvatar = ({
  variant = 'happy',
  size = 'md',
  pulse = false,
  showStatusDot = false,
  className = '',
  alt = 'Maya',
  ...rest
}) => {
  const px = SIZE_PX[size] || SIZE_PX.md;
  const safeVariant = VARIANTS[variant] ? variant : 'happy';
  const src = VARIANTS[safeVariant];
  const ringColor = RING_BY_VARIANT[safeVariant];
  const dotColor = STATUS_DOT_COLOR[safeVariant];
  const entry = ENTRY_ANIMATION[safeVariant];

  return (
    <span
      className={`relative inline-block ${className}`}
      style={{ width: px, height: px }}
      data-maya-variant={safeVariant}
      {...rest}
    >
      <span
        className={`absolute inset-0 rounded-full ring-2 ${ringColor} ring-offset-2 ring-offset-white pointer-events-none`}
        aria-hidden
      />
      <AnimatePresence mode="wait" initial={false}>
        <motion.img
          key={safeVariant}
          src={src}
          alt={alt}
          width={px}
          height={px}
          draggable={false}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{
            opacity: 1,
            scale: entry.scale,
            rotate: entry.rotate,
          }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            duration: safeVariant === 'celebrating' ? 0.55 : 0.4,
            ease: 'easeOut',
          }}
          className="absolute inset-0 w-full h-full rounded-full object-cover bg-slate-50 shadow-md select-none"
          loading="eager"
        />
      </AnimatePresence>

      {pulse && (
        <motion.span
          aria-hidden
          className={`absolute inset-0 rounded-full ${ringColor.replace('ring-', 'bg-')} opacity-30`}
          animate={{ scale: [1, 1.18, 1], opacity: [0.35, 0, 0.35] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeOut' }}
        />
      )}

      {showStatusDot && (
        <motion.span
          aria-hidden
          className={`absolute bottom-0 right-0 ${dotColor} rounded-full border-2 border-white shadow`}
          style={{ width: Math.max(8, px / 6), height: Math.max(8, px / 6) }}
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.4, 1] }}
          transition={{ type: 'spring', stiffness: 380, damping: 16 }}
        />
      )}
    </span>
  );
};

export default MayaAvatar;
export { VARIANTS, SIZE_PX };
